<!--
 * @Author: w9013327 v-maijiahua@oppo.com
 * @Date: 2022-12-01 16:39:23
 * @LastEditors: jordan.lu jordan.lu@oppo.com
 * @LastEditTime: 2024-03-20 16:00:26
 * @FilePath: \sns-user-frontend\src\components\commentDetail.vue
 * @Description: 评论详情
-->
<template>
  <ObPopup v-model="visible" height="calc(100vh - var(--orbit-nav-height))">
    <div class="comment-detail" :style="{ paddingBottom }">
      <CommentCard 
        v-if="commentInfo"
        v-model="commentInfo"
        :authorInfo="authorInfo"
        :actionPop="actionPop"
        ref="commentCardRef"
        @reply="onReply"
        @edit="editContent"
      />
      <BorderLine /> 
      <van-list
        v-if="commentInfo"
        v-model:loading="loading"
        :finished="direction === 'up' ? upFinished : finished"
        :error="error"
        :error-text="$t('error')"
        :loading-text="$t('loading')"
        finished-text=" "
        :offset="0"
        @load="load"
        :immediate-check="true"
        :direction="direction"
      >
        <CommentCard 
          v-for="(reply, index) in replyList"
          :key="reply.id" 
          :actionPop="actionPop"
          ref="replyCardRef"
          v-model="replyList[index]"
          cardType="comment"
          @reply="onReply"
          @edit="editContent"
          @updateSecondList="onUpdate"
        />
        <template #error>
          <pageStatus v-if="listStatus" :status="listStatus"/>
          <p v-else> 
            <span>{{ $t('error') }}</span>  
            <span @click="load" class="reload">{{ $t('reload') }}</span>
          </p>

        </template>
      </van-list>
      <div class="comment-detail-foot">
        <CommentEditor
          v-if="!props.closeStatus"
          :authorInfo="authorInfo" 
          :commentData="commentData"
          :threadInfo="threadInfo"
          :tid="props.tid"
          editorType="dialog"
          @update="onUpdate"
          @create="onCreate"
        />
        <div class="comment-close" v-else>
          {{$t('closeComment')}}
        </div>
      </div>
    </div>
  </ObPopup>
</template>

<script setup>
import BorderLine from './borderLine'
import CommentCard from './card/commentCard'
import CommentEditor from '@/components/web/commentEditor'

import http from '@/service'

import { watch, ref, reactive, onActivated, onDeactivated, onMounted, nextTick } from 'vue';

import { useRoute } from 'vue-router';
import { $throttle } from '@/utils/lodashFuncs';
import ObPopup from './common/obPopup.vue'

/* --------------------------------------------------------------------------*/

const emit = defineEmits(['update:modelValue', 'refresh'])

const route = useRoute()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  cid: {
    type: String,
    default: ''
  },
  rid: {
    type: String,
    default: ''
  },
  tid: {
    type: String,
    default: ''
  },
  commentData: {
    type: Object,
  },
  closeStatus: {
    type: Boolean,
    default: false
  },
  threadInfo: {
    type: Object,
    deafult: () => {}
  },
  actionPop: {
    type: Boolean,
    default: true
  },
  sortType: {
    type: Number,
    default: null
  },
})

// 评论卡组件引用
const commentCardRef = ref(null)
// 回复卡组件引用
const replyCardRef = ref(null)

// 抽屉开关
const visible = ref(false)

// 用于帖子引用文本的头像展示
const authorInfo = ref({})

// 评论数据
const commentInfo = ref(null)
// 回复列表
const replyList = ref([])

const paddingBottom = ref(0)

const refresh = ref(false)

const commentData = ref(null)

const listStatus = ref('')


// 查询页码
const page = ref(1)

const error = ref(false)
const loading = ref(false)  // 加载开关
const finished = ref(false) // 是否存在更多上滑数据
const upFinished = ref(false) // 是否存在更多下滑数据
const loadPrevPage = ref(false) // 是否需要加载上一页数据

let direction = ref('down') // 滚动方向
let oldScrollTop = ref(0) // 滚动距离


/* --------------------------------------------------------------------------*/


// 
let MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver
const mo = new MutationObserver((mutationList) => {
  const { height } = document.querySelector('.comment-detail-foot').getBoundingClientRect()
  paddingBottom.value = `${height}px`
})

// 查询评论
const getComment = async ({cid: id, tid}) => {
  const { code, data, msg } = await http.getComment({
    tid,
    id
  })
  if (code === 200 && data) {
    commentInfo.value = { ...data }
    const { uid, avatar, iconLink: icon, nickname } = data.toUserBasicInfo
    authorInfo.value = { id: uid, avatar, icon, nickname }
    upFinished.value = true
  } else {
    commentInfo.value = {}
  }
}

// 查询回复所在页码
const getReplyPage = async ({cid, rid}) => {
  const { code, data, msg } = await http.queryReplyPage({
    limit: 20,
    parentCid: cid,
    id: rid
  })
  const { replyPage } = data
  commentInfo.value = { ...data } // 关联评论
  const { uid, avatar, iconLink: icon, nickname } = data.toUserBasicInfo
  authorInfo.value = { id: uid, avatar, icon, nickname }
  upFinished.value = replyPage.page === 1 // 是否前面无更多数据
  loadPrevPage.value = replyPage.page !== 1 // 是否需要加载上一页
  page.value = replyPage.page || 1 // 回复所在页码
}

// 加载更多
const load = $throttle(async () => {
  error.value = false
  finished.value = false
  listStatus.value = ''
  let prevPageData = null
  let queryPage = page.value
  // 获取需要加载的页数，上滑取最后一条数据的页数处理，下滑取第一条
  if (replyList.value.length) {
    queryPage = direction.value === 'up' ? replyList.value[0].page - 1 : replyList.value[replyList.value.length - 1].page + 1
  }
  if (refresh.value) {
    queryPage = 1
    replyList.value = []
    refresh.value = false
  }
  const { code, data, msg } = await http.getReplyList({
    limit: 20,
    page: queryPage,
    parentCid: commentInfo.value.id || props.cid,
    needFeaturedStick: props.sortType === 3
  })
  let { rows = [], total = 0 } = data || {}
  if (code === 200 &&!rows.length && !total) {
    error.value = true    
  }
  if (replyList.value.length && code !== 200 ) {
    error.value = true
  }
  if (!rows.length && !replyList.value.length) {
    listStatus.value = code === 200 ? 'noContent' : 'error'
    error.value = true
  }
  // 数据绑定页数
  rows.forEach(r => {
    r.page = queryPage
  })
  // 如需要定位到具体回复位置，默认将上一页数据获取，解决定位第一条无滚动距离需要上滑改变滚动距离才触发加载上一页的问题
  if (props.rid && loadPrevPage.value && queryPage > 1) {
    prevPageData = await http.getReplyList({
      limit: 20,
      page: queryPage - 1,
      parentCid: commentInfo.value.id,
      needFeaturedStick: props.sortType === 3
    })
    if (prevPageData.code === 200 && prevPageData.data) {
      const prevRows = prevPageData.data.rows || []
      prevRows.forEach(r => {
        r.page = queryPage - 1
      })
      rows = [...prevRows, ...rows]
      // 是否加载完定位页数前的页码数据
      upFinished.value = (queryPage - 1) === 1
    }
  }
  if (direction.value === 'up') {
    // 加载前列表滚动高度
    const oldScrollHeight = document.querySelector('.van-list').scrollHeight
    // 插入数据
    replyList.value = [  ...rows, ...replyList.value ]
    nextTick(() => {
      // 加载后列表滚动高度
      const newScrollHeight = document.querySelector('.van-list').scrollHeight
      // 下滑加载数据完成后，设置滚动距离至加载前的位置
      document.querySelector('.el-drawer__body').scrollTop = newScrollHeight - (oldScrollHeight)
      // 如查询页码为第一页，扭转完成开关
      upFinished.value = queryPage === 1 
    })
  } else {
    replyList.value = [ ...replyList.value, ...rows ]
  }
  // // 图片懒加载
  // nextTick(() => {
  //   imgLazyLoad()
  // })
  // 加载结束
  loading.value = false
  // 如查询回复首次进入定位到查询的回复位置
  if (loadPrevPage.value || commentData.value?.id) {
    const locatedIndex = rows.findIndex(r => (r.id === props.rid) || (r.id === commentData.value?.id))
    nextTick(() => {
      try {
        replyCardRef.value[locatedIndex]?.cardRef?.scrollIntoView({
          block: 'center'
        })
        // 关闭定位开关
        loadPrevPage.value = false
      } catch {}
    })
  }
  // 防止上滑完成后下滑加载前面数据导致重新计算数据是否加载完
  if (finished.value) return
  finished.value = queryPage >= Math.ceil(total / 20)
}, 500)

// 判断滚动方向
const handleScroll = (e) => {
  let scrollTop = e.target.scrollTop
  let scroll = scrollTop - oldScrollTop.value;
  oldScrollTop.value = scrollTop;
  
  if (scroll < 0) {
    // 下滑无更多数据强制转为上滑，不展示顶部的无更多
    direction.value = upFinished.value ? 'down' : 'up'
  } else {
    direction.value = 'down'
  }
}


const editContent = (data) => {
  data.isEdit = true
  commentData.value = { ...data }
}

const onCreate = () => {
  if (commentData.value?.id === commentInfo.value.id) {
    setTimeout(() => {
      // 存在回复id则进行回复定位
      const params = {
        cid: props.cid,
        rid: props.rid,
        tid: props.tid
      }
      !props.rid ? getComment(params) : getReplyPage(params)
    }, 1000)
  } 
  commentData.value = null
  emit('refresh')
  setTimeout(() => {
    refresh.value = true
    load()
  }, 1000)
}

const onUpdate = () => {
  onCreate()
}

const onReply = (data) => {
  commentData.value = { ...data }
}
/* --------------------------------------------------------------------------*/

watch(() => visible, (val) => {
  if (val.value) {
    // 存在回复id则进行回复定位
    const params = {
      cid: props.cid,
      rid: props.rid,
      tid: props.tid
    }
    !props.rid ? getComment(params) : getReplyPage(params)
    // 监听滚动判断上下滑
    window.addEventListener('scroll', handleScroll, true);
  } else {
    // 隐藏抽屉，重置数据
    page.value = 1
    commentInfo.value = null
    replyList.value = []
    replyCardRef.value = null
    commentCardRef.value = null
    loadPrevPage.value = false
    loading.value = false
    finished.value = false
    error.value = false
    direction.value = 'down'
    oldScrollTop.value = 0
    window.removeEventListener('scroll', handleScroll, true)
    emit('update:modelValue', val.value)
  }
}, {
  immediate: true,
  deep: true
})

// 同步开关
watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    nextTick(() => {
      mo.observe(document.querySelector('.comment-detail-foot'), { 
        childList: true, // 子节点的变动（新增、删除或者更改）
        attributes: true, // 属性的变动
        characterData: true, // 节点内容或节点文本的变动
        subtree: true,
        attributeFilter: ['style'], 
        attributeOldValue: true 
      })
    })
  } else {
    mo.disconnect()
  }
}, {
  immediate: true
})

watch(() => props.commentData, (val) => {
  commentData.value = val
}, {
  immediate: true,
  deep: true
})

onMounted(() => {
  
})

</script>

<style lang="postcss" scoped>
.comment-detail {
  :deep(.comment-card) {
    width: 792px !important;
    margin: 48px auto 0 !important;
    padding: 24px 40px;
    box-sizing: border-box;
  }
  .delete-content {
    text-align: center;
    padding-bottom: 32px;
  }
}
.border-line {
  width: 792px;
  margin: 0 auto;
}
.comment-detail-foot {
  position: fixed;
  left: 50%;
  bottom: 0;
  width: 792px;
  padding: 0 16px 24px;
  margin-left: calc((-1 * 386px) - var(--scrollbar));
  background: #FFF;
}
.reload {
  cursor: pointer;
  margin-left: 10px;
  color: var(--text-hint);
  &:hover {
    color: var(--theme);
  }
}

.comment-close {
  font-size: 18px;
  color: var(--text-hint);
  text-align: center;
  line-height: 150%;
  padding: 16px;
  margin: 56px 0 48px 0;
  background: var(--background-plain);
  position: relative;
  &::before,&::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 100%;
    top: 0;
    background: var(--background-plain);
    border-radius: 8px 0 0 8px;
  }
  &::before {
    left: -16px;
  }
  &::after {
    right: -16px;
    border-radius: 0 8px 8px 0;
  }
}
</style>